import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Search, ShoppingCart, ChevronDown, ChevronRight, User, Phone, Mail, Menu, X, Sun, MapPin, Eye, Package, Zap } from "lucide-react";
import { getIcon } from "../utils/icons";
import { useCart } from "../context/CartContext";
import { useTheme } from "../context/ThemeContext";
import { useCustomerAuth } from "../context/CustomerAuthContext";
import { getCategories, getCategoryTree, getCmsPages } from "../lib/api";
import api from "../lib/api";
import PoptavkaModal from "./PoptavkaModal";
import MegaMenu from "./MegaMenu";
import NavbarSearch from "./NavbarSearch";

/* ── Navbar wrapper: isolate from html zoom-responsive font-size ────── */
const NAV_STYLE = { fontSize: "16px", lineHeight: "1.4" };

export default function Navbar() {
    const { items: cart, itemCount: cartCount, subtotal: cartTotal } = useCart();
    const { theme } = useTheme();
    const { customer } = useCustomerAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchQuery, setSearchQuery] = useState("");
    const [mobileOpen, setMobileOpen] = useState(false);
    const [categoryTree, setCategoryTree] = useState([]);
    const [cartHover, setCartHover] = useState(false);
    const [cmsPages, setCmsPages] = useState([]);
    const [watchCount, setWatchCount] = useState(0);
    const [navSettings, setNavSettings] = useState(null);
    const [poptavkaOpen, setPoptavkaOpen] = useState(false);

    // Refresh categories on route change (catches admin deletions)
    useEffect(() => {
        getCategoryTree(true).then(r => setCategoryTree(r.data || [])).catch(() => {});
        getCmsPages(true).then(r => setCmsPages(r.data || [])).catch(() => {});
        api.get("/nav-settings").then(r => setNavSettings(r.data)).catch(() => {});
    }, [location.pathname]);

    useEffect(() => {
        if (customer) {
            api.get("/customer/watchdog/count").then(r => setWatchCount(r.data?.count || 0)).catch(() => {});
        } else { setWatchCount(0); }
    }, [customer]);

    const handleSearch = (e) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            navigate(`/kategorie?q=${encodeURIComponent(searchQuery.trim())}`);
            setSearchQuery("");
            setMobileOpen(false);
        }
    };

    const grayItems = navSettings?.gray_bar_items?.filter(i => i.visible) || [];
    const greenH = navSettings?.green_bar_height || 48;
    const greenIconSize = navSettings?.green_bar_icon_size || 16;
    const greenFontSize = navSettings?.green_bar_font_size || 13;

    return (
        <header className="w-full shadow-sm sticky top-0 z-[200]" style={NAV_STYLE} data-testid="site-header">
            {/* ── Top Info Bar (announcement) ─────────────────────────── */}
            {theme.show_top_bar && theme.top_bar_text && (
                <div className="bg-green-700 text-white text-center py-1.5 text-xs font-medium tracking-wide" data-testid="top-info-bar">
                    <div className="sg-container">{theme.top_bar_text}</div>
                </div>
            )}

            {/* ── Gray Utility Bar ─────────────────────────── */}
            {navSettings?.gray_bar_visible !== false && (
                <div className="bg-gray-50 border-b border-gray-100 hidden md:block" style={{ fontSize: "11px" }}>
                    <div className="sg-container flex items-center justify-between" style={{ height: "32px", paddingTop: 0, paddingBottom: 0 }}>
                        <div className="flex items-center gap-5 text-gray-500">
                            <a href={`tel:${theme.phone}`} className="flex items-center gap-1 hover:text-green-600 transition-colors" data-testid="contact-phone">
                                <Phone size={10} className="text-green-600" />
                                <span className="font-medium">{theme.phone}</span>
                            </a>
                            {theme.email && (
                                <a href={`mailto:${theme.email}`} className="flex items-center gap-1 hover:text-green-600 transition-colors" data-testid="contact-email">
                                    <Mail size={10} className="text-green-600" />
                                    <span>{theme.email}</span>
                                </a>
                            )}
                            <span className="flex items-center gap-1" data-testid="contact-address">
                                <MapPin size={10} className="text-green-600" />
                                <span>{theme.address || "Kladno, Stredocesky kraj"}</span>
                            </span>
                        </div>
                        <div className="flex items-center gap-4 text-gray-500">
                            {grayItems.map((gi, idx) => (
                                <Link key={idx} to={gi.url || "#"} className="hover:text-green-600 transition-colors font-medium" data-testid={`gray-nav-${idx}`}>
                                    {gi.label}
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* ── Main Header ─────────────────────────── */}
            <div className="bg-white border-b border-gray-200" style={{ padding: "12px 0" }}>
                <div className="sg-container flex items-center gap-5" style={{ paddingTop: 0, paddingBottom: 0 }}>
                    {/* Logo */}
                    <Link to="/" className="flex-shrink-0" data-testid="site-logo">
                        {theme.logo_url ? (
                            <img src={theme.logo_url} alt="Solar-Green" style={{ height: "66px", maxWidth: "280px" }} className="w-auto" />
                        ) : (
                            <span style={{ display: "flex", alignItems: "center", fontFamily: "Arial Black, Arial, sans-serif", fontWeight: 900, fontSize: "34px", letterSpacing: "-1px", gap: 0 }}>
                                <span style={{ color: "#E8C800" }}>S</span>
                                <Sun size={34} style={{ color: "#F9D932", margin: "0 1px" }} />
                                <span style={{ color: "#E8C800" }}>LAR</span>
                                <span style={{ color: "var(--sg-primary)" }}>GREEN</span>
                            </span>
                        )}
                    </Link>

                    {/* Search with autocomplete */}
                    <div className="hidden sm:block flex-1 max-w-md">
                        <NavbarSearch onSearch={(q) => {
                            if (q.trim()) navigate(`/kategorie?q=${encodeURIComponent(q.trim())}`);
                        }} />
                    </div>

                    {/* Phone CTA - visible from md */}
                    <a href={`tel:${theme.phone}`} className="hidden md:flex items-center gap-2.5 flex-shrink-0 px-3 py-2 rounded-xl bg-green-50 hover:bg-green-100 transition-colors group" data-testid="header-phone-cta">
                        <div className="w-9 h-9 rounded-full bg-green-600 flex items-center justify-center">
                            <Phone size={15} className="text-white" />
                        </div>
                        <div className="text-left hidden lg:block">
                            <p style={{ fontSize: "10px" }} className="text-gray-500 leading-tight">Zavolejte nám</p>
                            <p style={{ fontSize: "14px" }} className="font-bold text-green-700 leading-tight">{theme.phone || "+420 608 808 815"}</p>
                        </div>
                    </a>

                    {/* Poptej reseni CTA - visible from md */}
                    <button onClick={() => setPoptavkaOpen(true)}
                        className="hidden md:flex items-center gap-2 flex-shrink-0 px-4 py-2.5 rounded-xl bg-yellow-50 hover:bg-yellow-100 border border-yellow-200 transition-colors"
                        data-testid="header-poptavka-cta">
                        <Zap size={16} className="text-yellow-600" />
                        <span style={{ fontSize: "13px" }} className="font-bold text-yellow-700 hidden lg:inline">Poptej řešení</span>
                    </button>

                    {/* Account */}
                    {customer ? (
                        <div className="hidden md:flex items-center gap-2 flex-shrink-0">
                            <Link to="/muj-ucet" className="flex flex-col items-center gap-0.5 text-green-600 hover:text-green-700 transition-colors" data-testid="header-account-btn">
                                <div className="w-9 h-9 rounded-full bg-green-100 flex items-center justify-center">
                                    <User size={16} className="text-green-700" />
                                </div>
                                <span style={{ fontSize: "10px" }} className="font-semibold max-w-[70px] truncate">{customer.name?.split(' ')[0] || 'Účet'}</span>
                            </Link>
                            <Link to="/muj-ucet" className="flex flex-col items-center gap-0.5 text-amber-600 hover:text-amber-700 transition-colors relative" data-testid="header-watchdog-btn">
                                <div className="w-9 h-9 rounded-full bg-amber-50 flex items-center justify-center relative">
                                    <Eye size={16} className="text-amber-600" />
                                    {watchCount > 0 && (
                                        <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] rounded-full bg-amber-500 text-white flex items-center justify-center px-0.5"
                                            style={{ fontSize: "9px", fontWeight: 700 }} data-testid="watchdog-badge">{watchCount}</span>
                                    )}
                                </div>
                                <span style={{ fontSize: "10px" }} className="font-semibold">Hlídám</span>
                            </Link>
                        </div>
                    ) : (
                        <Link to="/prihlaseni" className="hidden md:flex flex-col items-center gap-1 text-gray-500 hover:text-green-600 flex-shrink-0 transition-colors" data-testid="header-account-btn">
                            <User size={22} strokeWidth={1.5} />
                            <span style={{ fontSize: "10px" }} className="font-medium">Přihlásit</span>
                        </Link>
                    )}

                    {/* Cart */}
                    <div className="hidden md:block relative" onMouseEnter={() => setCartHover(true)} onMouseLeave={() => setCartHover(false)}>
                        <Link to="/kosik" className="flex flex-col items-center gap-1 text-gray-500 hover:text-green-600 flex-shrink-0 transition-colors" data-testid="header-cart-btn">
                            <div className="relative">
                                <ShoppingCart size={24} strokeWidth={1.5} />
                                {cartCount > 0 && (
                                    <span className="absolute -top-1.5 -right-2 min-w-[18px] h-[18px] rounded-full text-white flex items-center justify-center px-0.5 bg-red-500 shadow-sm"
                                        style={{ fontSize: "10px", fontWeight: 700 }} data-testid="cart-badge">{cartCount}</span>
                                )}
                            </div>
                            <span style={{ fontSize: "10px" }} className="font-medium">Košík</span>
                        </Link>
                        {cartHover && cartCount > 0 && (
                            <div className="absolute right-0 top-full mt-2 bg-white rounded-xl shadow-2xl border border-gray-200 p-4 z-[300]"
                                style={{ width: "280px", boxShadow: "0 12px 40px rgba(0,0,0,0.15)" }} data-testid="cart-hover-popup">
                                <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-100">
                                    <span style={{ fontSize: "13px" }} className="font-bold text-gray-800">Kosik</span>
                                    <span style={{ fontSize: "11px" }} className="text-gray-400">{cartCount} polozek</span>
                                </div>
                                <div className="space-y-2 max-h-40 overflow-y-auto mb-3">
                                    {cart?.slice(0, 3).map((item, i) => (
                                        <div key={i} className="flex items-center gap-2" style={{ fontSize: "11px" }}>
                                            <span className="w-5 h-5 bg-green-50 rounded text-green-600 font-bold flex items-center justify-center flex-shrink-0" style={{ fontSize: "9px" }}>{item.quantity}x</span>
                                            <span className="text-gray-600 truncate flex-1">{item.name}</span>
                                            <span className="text-gray-800 font-semibold flex-shrink-0">{((item.sale_price || item.price) * item.quantity).toLocaleString("cs-CZ")} Kc</span>
                                        </div>
                                    ))}
                                </div>
                                <div className="flex items-center justify-between pt-2 border-t border-gray-100 mb-3">
                                    <span style={{ fontSize: "13px" }} className="font-bold text-gray-800">Celkem:</span>
                                    <span style={{ fontSize: "13px" }} className="font-bold text-green-600">{cartTotal.toLocaleString("cs-CZ")} Kc</span>
                                </div>
                                <Link to="/kosik" className="block w-full text-center py-2 bg-green-600 text-white font-bold rounded-lg hover:bg-green-700 transition-colors"
                                    style={{ fontSize: "12px" }}>
                                    Prejit do kosiku
                                </Link>
                            </div>
                        )}
                    </div>

                    {/* Mobile: phone + hamburger */}
                    <div className="flex items-center gap-2 md:hidden">
                        <a href={`tel:${theme.phone}`} className="p-1.5 rounded-lg bg-green-50 hover:bg-green-100" data-testid="mobile-phone-btn">
                            <Phone size={18} className="text-green-600" />
                        </a>
                        <button className="p-1.5" onClick={() => setMobileOpen(v => !v)} data-testid="hamburger-btn">
                            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
                        </button>
                    </div>
                </div>
            </div>

            {/* ── Green Category Bar + Mega Menu ─────────── */}
            <MegaMenu
                categoryTree={categoryTree}
                navSettings={navSettings}
                greenH={greenH}
                iconSize={greenIconSize}
                fontSize={greenFontSize}
            />

            {/* ── Mobile Drawer ─────────────────────── */}
            {mobileOpen && <div className="fixed inset-0 bg-black/40 z-[250] md:hidden" onClick={() => setMobileOpen(false)} data-testid="mobile-menu-overlay" />}
            <MobileDrawer
                open={mobileOpen}
                onClose={() => setMobileOpen(false)}
                categoryTree={categoryTree}
                customer={customer}
                cartCount={cartCount}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                onSearch={handleSearch}
                theme={theme}
                onPoptavkaOpen={() => { setMobileOpen(false); setPoptavkaOpen(true); }}
            />

            {/* ── Poptavka Modal (iframe) ─────────── */}
            <PoptavkaModal open={poptavkaOpen} onClose={() => setPoptavkaOpen(false)} />
        </header>
    );
}


/* ═══════════════════════════════════════════════════════════════════
   MOBILE DRAWER
   ═══════════════════════════════════════════════════════════════════ */
function MobileDrawer({ open, onClose, categoryTree, customer, cartCount, searchQuery, setSearchQuery, onSearch, theme, onPoptavkaOpen }) {
    const [expanded, setExpanded] = useState({});
    const toggle = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

    return (
        <div className={`fixed top-0 left-0 bottom-0 z-[260] bg-white shadow-2xl transition-transform duration-300 md:hidden overflow-hidden flex flex-col ${open ? "translate-x-0" : "-translate-x-full"}`}
            style={{ width: "min(300px, 85vw)", fontSize: "14px" }}
            data-testid="mobile-menu-drawer">
            {/* Header */}
            <div className="flex items-center justify-between px-3 py-2.5 border-b" style={{ background: "var(--sg-primary)" }}>
                <Link to="/" className="flex items-center" style={{ fontFamily: "Arial Black", fontWeight: 900, fontSize: "18px", gap: 0 }} onClick={onClose}>
                    <span style={{ color: "#E8C800" }}>S</span>
                    <Sun size={18} style={{ color: "#F9D932", margin: "0 1px" }} />
                    <span style={{ color: "#E8C800" }}>LAR</span>
                    <span style={{ color: "white" }}>GREEN</span>
                </Link>
                <button onClick={onClose} className="p-1 rounded text-white/80 hover:text-white" data-testid="mobile-menu-close">
                    <X size={20} />
                </button>
            </div>

            {/* Search */}
            <div className="px-3 py-2 border-b">
                <form onSubmit={onSearch} className="flex border rounded overflow-hidden" style={{ borderColor: "var(--sg-primary)", height: "34px" }}>
                    <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                        placeholder="Hledat..."
                        className="flex-1 px-3 outline-none" style={{ fontSize: "13px" }}
                        data-testid="mobile-search-input" />
                    <button type="submit" className="px-3 text-white" style={{ backgroundColor: "var(--sg-primary)" }} data-testid="mobile-search-btn">
                        <Search size={14} />
                    </button>
                </form>
            </div>

            {/* Quick links */}
            <div className="flex border-b divide-x" style={{ fontSize: "11px" }}>
                <Link to={customer ? "/muj-ucet" : "/prihlaseni"} className="flex-1 flex items-center justify-center gap-1 py-2 text-gray-600 hover:bg-gray-50" onClick={onClose} data-testid="mobile-account-link">
                    <User size={13} /> {customer ? "Ucet" : "Prihlasit"}
                </Link>
                <Link to="/kosik" className="flex-1 flex items-center justify-center gap-1 py-2 text-gray-600 hover:bg-gray-50" onClick={onClose} data-testid="mobile-cart-link">
                    <ShoppingCart size={13} /> Kosik {cartCount > 0 && <span className="text-white rounded-full w-4 h-4 flex items-center justify-center bg-red-500" style={{ fontSize: "8px" }}>{cartCount}</span>}
                </Link>
            </div>

            {/* Poptej reseni mobile */}
            <button onClick={onPoptavkaOpen}
                className="flex items-center justify-center gap-2 py-2.5 bg-yellow-50 border-b border-yellow-100 text-yellow-700 font-bold w-full"
                style={{ fontSize: "12px" }}
                data-testid="mobile-poptavka">
                <Zap size={14} /> Poptej reseni
            </button>

            {/* Categories */}
            <div className="flex-1 overflow-y-auto" data-testid="mobile-category-list">
                <div className="px-3 pt-2 pb-1">
                    <span className="text-gray-400 font-bold uppercase tracking-wider" style={{ fontSize: "9px" }}>Kategorie</span>
                </div>
                {categoryTree.filter(c => !c.parent_id).map(cat => {
                    const IconComp = getIcon(cat.icon);
                    const hasChildren = cat.children && cat.children.length > 0;
                    const isOpen = expanded[cat.id];
                    return (
                        <div key={cat.id} data-testid={`mobile-cat-${cat.slug}`}>
                            <div className="flex items-center border-b border-gray-100">
                                <Link to={`/kategorie/${cat.slug}`}
                                    className="flex-1 flex items-center gap-2 px-3 py-2 hover:bg-green-50 transition-colors"
                                    style={{ fontSize: "13px" }}
                                    onClick={onClose}>
                                    <span className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0" style={{ background: "var(--sg-primary)", color: "white" }}>
                                        {cat.image_url ? <img src={cat.image_url} alt="" className="w-3 h-3 object-contain" /> : <IconComp size={10} />}
                                    </span>
                                    <span className="font-semibold text-gray-800 truncate">{cat.name}</span>
                                </Link>
                                {hasChildren && (
                                    <button onClick={() => toggle(cat.id)} className="px-3 py-2 text-gray-400 hover:text-gray-700" data-testid={`mobile-cat-toggle-${cat.slug}`}>
                                        <ChevronDown size={14} className={`transition-transform ${isOpen ? "rotate-180" : ""}`} />
                                    </button>
                                )}
                            </div>
                            {hasChildren && isOpen && (
                                <div className="bg-gray-50/50">
                                    {cat.children.map(ch => {
                                        const ChIcon = getIcon(ch.icon);
                                        const hasGC = ch.children && ch.children.length > 0;
                                        const gcOpen = expanded[ch.id];
                                        return (
                                            <div key={ch.id}>
                                                <div className="flex items-center border-b border-gray-100">
                                                    <Link to={`/kategorie/${ch.slug}`}
                                                        className="flex-1 flex items-center gap-2 py-2 hover:bg-green-50 transition-colors"
                                                        style={{ paddingLeft: "28px", fontSize: "12px" }}
                                                        onClick={onClose}>
                                                        <span className="w-4 h-4 rounded bg-gray-200 text-gray-500 flex items-center justify-center flex-shrink-0">
                                                            <ChIcon size={9} />
                                                        </span>
                                                        <span className="text-gray-600 truncate">{ch.name}</span>
                                                    </Link>
                                                    {hasGC && (
                                                        <button onClick={() => toggle(ch.id)} className="px-3 py-2 text-gray-400">
                                                            <ChevronDown size={12} className={`transition-transform ${gcOpen ? "rotate-180" : ""}`} />
                                                        </button>
                                                    )}
                                                </div>
                                                {hasGC && gcOpen && (
                                                    <div className="bg-gray-100/50">
                                                        {ch.children.map(gc => (
                                                            <Link key={gc.id} to={`/kategorie/${gc.slug}`}
                                                                className="flex items-center gap-1.5 py-1.5 border-b border-gray-100 hover:bg-green-50 transition-colors text-gray-500"
                                                                style={{ paddingLeft: "44px", fontSize: "11px" }}
                                                                onClick={onClose}>
                                                                <ChevronRight size={8} className="text-gray-300" />
                                                                {gc.name}
                                                            </Link>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Bottom: phone */}
            <div className="border-t px-3 py-2" style={{ fontSize: "11px" }}>
                <a href={`tel:${theme.phone}`} className="flex items-center gap-2 text-green-700 font-bold">
                    <Phone size={12} /> {theme.phone}
                </a>
            </div>
        </div>
    );
}
